import { annualizedRevenueData } from "../../../brandDetailsData.ts";
import { useFormContext } from "react-hook-form";
import React, { useEffect, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../stores/hooks/hooks.ts";
import {
  RevAlert,
  RevButton,
  RevCard,
  RevDialog,
  RevInput,
  RevTooltip,
  RevSelect,
} from "@revmatics/catalyst";
import { editBrandById } from "../../../../../stores/thunks/brandThunk.tsx";
import { BrandEditByIdBodyType } from "../../../../../stores/types/brandTypes.tsx";
import {
  hasValidDomain,
  isValidUrl,
} from "../../../../../helpers/functions/hasValidHelpers.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-light-svg-icons";
import { useTrimmedRegister } from "../../../../../helpers/hooks/useTrimmedRegister.ts";

type Props = {
  readAccessOnly: boolean;
};

export const DetailsView = ({ readAccessOnly }: Props) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<BrandEditByIdBodyType>();

  const dispatch = useAppDispatch();
  const [isOpenEditWebsite, setOpenEditWebsite] = useState(false);
  const [website, setWebsite] = useState("");
  const isLoading = useAppSelector((state) => state.brand.isLoadingEditById);
  const brandById = useAppSelector((state) => state.brand.brandById);

  useEffect(() => {
    if (brandById) {
      setWebsite(brandById.website);
    }
  }, [brandById]);

  const handleEditWebsite = () => {
    if (website && brandById && website !== brandById.website) {
      let newWebsite = website;
      const formDataForApi = new FormData();
      if (website && hasValidDomain(website) && !isValidUrl(website)) {
        newWebsite = "https://" + website;
      }
      formDataForApi.append("website", newWebsite);

      dispatch(
        editBrandById({ id: brandById.id.toString(), body: formDataForApi }),
      ).finally(() => {
        setOpenEditWebsite(false);
      });
    }
  };
  const registerTrimmed = useTrimmedRegister(register);

  return (
    <RevCard
      title="General"
      text="This is basic information about your brand."
      className="grid grid-cols-1 gap-6 md:grid-cols-2"
      paddingTitle="py-4 px-6"
    >
      <RevInput
        name="name"
        label="Brand Name"
        placeholder="Enter Brand Name"
        type="text"
        size="md"
        required
        register={registerTrimmed("name", {
          required: "Name is required.",
        })}
        error={errors.name}
        isLabelBold
        disabled={readAccessOnly}
      />
      <div className="relative">
        <RevInput
          name="website"
          label="Brand Website"
          placeholder="Enter Brand Website"
          type="text"
          size="md"
          register={register("website")}
          error={errors.website}
          disabled={!!brandById}
          isLabelBold
        />
        {!brandById && (
          <div className="absolute -top-[2px] left-25 group">
            <RevTooltip
              content="Brand website is used to generate AI Personas"
              position="bottom"
            >
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="text-monochrome-600 hover:text-primary-500"
              />
            </RevTooltip>
          </div>
        )}
        {!!brandById && !readAccessOnly ? (
          <RevButton
            text="Edit Website"
            displayAs="link"
            handleClick={() => setOpenEditWebsite(true)}
            linkFontSize="extraSmall"
            className="absolute top-0 right-0 -translate-y-[3px]"
          />
        ) : (
          <RevTooltip
            content="Contact an Organization Administrator to use this feature."
            position="bottomRight"
          >
            <span
              data-testid="edit-website-brand-tooltip"
              className="typography-body-xs border-b border-primary-300 text-primary-500 hover:text-primary-600 no-underline focus:outline-none absolute top-0 right-0 -translate-y-[3px]"
            >
              Edit Website
            </span>
          </RevTooltip>
        )}
      </div>

      {brandById && (
        <RevSelect
          name="annualized_revenue"
          label="Annualized Revenue"
          options={annualizedRevenueData}
          control={control}
          error={errors.annualized_revenue}
          size="md"
          placeholder="Select"
          isLabelBold
          disabled={readAccessOnly}
        />
      )}
      <RevDialog
        isOpen={isOpenEditWebsite}
        title="Edit Website"
        setIsOpen={() => setOpenEditWebsite(false)}
        btnList={[
          <RevButton
            text="Cancel"
            variant="secondary"
            handleClick={() => setOpenEditWebsite(false)}
            key="brand-edit-cancel-website-edit"
          />,
          <RevButton
            text="Update Website"
            variant="primary"
            handleClick={handleEditWebsite}
            isLoading={isLoading}
            disabled={!hasValidDomain(website)}
            key="brand-edit-save-website-edit"
          />,
        ]}
      >
        <div className="space-y-2.5 w-full">
          <RevAlert
            data={{
              heading: "Warning",
              text: "This change updates critical data that feeds our AI platform and will result in additional processing time.",
              type: "warning",
              id: "edit-website-id",
              show: true,
            }}
          />
          <RevInput
            name="website"
            label="Brand website"
            type="url"
            size="md"
            required
            value={website}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setWebsite(e.target.value)
            }
          />
        </div>
      </RevDialog>
    </RevCard>
  );
};
